/**
 * Phase 24 / Plan 24-06 / Requirements EUA-05, EUA-06, EUA-09 /
 *   Decisions D-06, D-07, D-10, D-13
 *
 * State-discriminated Return-Card-Komponente für die EUA-Mollie-Return-Page.
 *
 * KRITISCH (PATTERNS.md Pitfall 7): Outer-Wrapper ist ein plain <div>, KEIN
 * Ant-<Card> — sonst überschreiben Ant-Card-Styles die Sketch-Tokens.
 *
 * KRITISCH (i18n-Interpolation): emailHint nutzt <Trans> mit components-Prop
 * für <strong>{{email}}</strong>-Interpolation (HTML würde sonst escaped).
 *
 * Polling/Retry/Timer-Logik liegt in `PaymentReturn.tsx` — diese Komponente
 * ist rein präsentational und hat keine Timer (React 18 StrictMode safe).
 */

import React from "react";
import { Link } from "react-router-dom";
import { Button } from "antd";
import { useTranslation, Trans } from "react-i18next";
import { MolliePaymentStatus } from "../../generated/graphql";
import { mapMollieFailureReasonToKey } from "../CreateBooking/helpers/mollieErrorCodeMapping";
import { URL_BASE } from "../../models/url";
import { BankTransferDetailsSection } from "./BankTransferDetails";
import css from "./MolliePaymentReturnCard.module.css";

export type ReturnCardState = "success" | "failure" | "pending" | "timeout" | "accessDenied" | "choosePayment";

export interface MolliePaymentReturnCardBooking {
    bookingNumber: string;
    attendeeFullName: string;
    courseName: string;
    grossPrice: number;
    paymentMethodLabel: string;
    paidAt?: Date | null;
    bookerEmail?: string;
    /** Rabatt der Buchung (z. B. Rabattcode), der Name kommt vom Server („Rabattcode ALLE10“). */
    discount?: number | null;
    discountName?: string | null;
}

export interface MolliePaymentReturnCardProps {
    state: ReturnCardState;
    status?: MolliePaymentStatus | null;
    booking?: MolliePaymentReturnCardBooking | null;
    onRetry?: () => void | Promise<void>;
    retryLoading?: boolean;
    onManualRefetch?: () => void | Promise<void>;
    /**
     * Phase 26.1 / Fix #3 — Cross-Device-Retry-Slot.
     *
     * Wenn der Bucher ueber die Reminder-Mail auf einem anderen Geraet/Browser
     * zurueckkommt, fehlt der `mollie-pending-payment`-Eintrag im localStorage
     * (Method, locale). In dem Fall rendert die Page einen Method-Picker
     * (`MollieMethodsBlock` controlled-mode) statt einer direkten Retry-Button-Aktion.
     *
     * Wenn `methodPicker` gesetzt ist, wird der primaere Retry-Button NICHT
     * gerendert — der Picker bringt sein eigenes Submit-Verhalten mit.
     */
    methodPicker?: React.ReactNode;
    /**
     * Ausgeloggter Bucher aus dem Dispatch-Zahlungslink (Reminder-Mail).
     *
     * Retry/Method-Picker sind auth-pflichtig — fuer ausgeloggte Bucher liefert
     * die Public-Status-Query weder bookingId noch availableMollieMethods. Statt
     * einer Sackgasse (nur "Später bezahlen") bekommt der Bucher einen primaeren
     * "Anmelden & jetzt bezahlen"-Button. Der Klick leitet ihn zum Login und nach
     * erfolgreichem Login wieder auf DIESE Return-Page zurueck (`fromUrl`), wo der
     * eingeloggte Retry-/Method-Picker-Pfad das eigentliche Bezahlen uebernimmt.
     *
     * Wird nur im Failure-Branch und nur gerendert, wenn weder `onRetry` noch
     * `methodPicker` gesetzt sind (gegenseitig exklusiv: ausgeloggt vs. eingeloggt).
     */
    onLoginToPay?: () => void;
    /**
     * Phase 26.1 / Fix #3 — Optional: Kurs-Header-Info fuer Cross-Device-Kontext.
     * Wird ueber den booking-Order-Box gerendert, wenn kein `booking`-Objekt
     * vorliegt aber das Backend zumindest courseTitle/courseStartDate liefert.
     */
    courseContext?: {
        courseTitle?: string | null;
        courseStartDate?: Date | null;
    } | null;
    /**
     * Optionaler Kontext-Hinweis ueber der Buchungs-Box (z.B. "In diesem Kurs ist
     * gerade ein Platz frei geworden" beim Inverse-Race aus dem Wartelisten-Modus).
     *
     * Bewusst ein Slot statt eines Flags: Die Card kennt den Grund nicht, die
     * aufrufende Page schon. Wird nur gerendert, wenn gesetzt.
     */
    notice?: React.ReactNode;
}

interface RowProps {
    label: string;
    value: React.ReactNode;
}

function Row({ label, value }: RowProps): JSX.Element {
    return (
        <div className={`${css.orderRow} order-row orderRow`}>
            <span className={`${css.orderLabel} order-label`}>{label}</span>
            <span className={`${css.orderValue} order-value`}>{value}</span>
        </div>
    );
}

function formatAmount(grossPrice: number): string {
    try {
        return new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
        }).format(grossPrice);
    } catch {
        return `${grossPrice.toFixed(2)} EUR`;
    }
}

function formatPaidAt(paidAt: Date): string {
    try {
        return new Intl.DateTimeFormat("de-DE", {
            dateStyle: "medium",
            timeStyle: "short",
        }).format(paidAt);
    } catch {
        return paidAt.toISOString();
    }
}

function formatCourseStart(date: Date): string {
    try {
        return new Intl.DateTimeFormat("de-DE", {
            dateStyle: "medium",
        }).format(date);
    } catch {
        return date.toISOString();
    }
}

export function MolliePaymentReturnCard(props: MolliePaymentReturnCardProps): JSX.Element {
    const { t } = useTranslation();
    const {
        state,
        status,
        booking,
        onRetry,
        retryLoading,
        onManualRefetch,
        methodPicker,
        onLoginToPay,
        courseContext,
        notice,
    } = props;

    // Stabile Sketch-Token-Klassen (kebab-case) als zusätzlicher Hook, damit
    // DOM-Queries (Tests + E2E + Sketch-Visual-Compare) ohne CSS-Module-Resolution
    // funktionieren. CSS-Module-Klassen (camelCase) liefern das Styling im Build.
    const iconStateClass =
        state === "success"
            ? `${css.successIcon} return-success-icon successIcon`
            : state === "failure" || state === "accessDenied"
                ? `${css.errorIcon} return-error-icon errorIcon`
                : `${css.pendingIcon} return-pending-icon pendingIcon`;

    const iconChar =
        state === "success" ? "✓" /* ✓ */
            : state === "failure" ? "✗" /* ✗ */
                : state === "accessDenied" ? "🔒" /* 🔒 */
                    : state === "choosePayment" ? "💳" /* 💳 */
                        : "⏳" /* ⏳ */;

    const cardKey = `${state}Card`;
    const titleKey = `mollieCheckout.returnPage.${cardKey}.title`;
    const subtitleKey = `mollieCheckout.returnPage.${cardKey}.subtitle`;

    const showBankTransferSection =
        state === "pending" && status && status.bankTransferDetails;

    return (
        <div className={`${css.euaReturnCard} eua-return-card`}>
            <div
                className={`${css.resultIcon} return-result-icon resultIcon ${iconStateClass}`}
                aria-hidden="true"
            >
                {iconChar}
            </div>

            <h2 className={`${css.title} return-title`}>{t(titleKey)}</h2>
            <p className={`${css.subtitle} return-subtitle`}>{t(subtitleKey)}</p>

            {notice && (
                <div
                    className={`${css.noticeBox} notice-box`}
                    data-testid="payment-return-notice"
                >
                    {notice}
                </div>
            )}

            {!booking && courseContext && (courseContext.courseTitle || courseContext.courseStartDate) && (
                <div
                    className={`${css.orderBox} order-box orderBox`}
                    data-testid="payment-return-course-context"
                >
                    {courseContext.courseTitle && (
                        <Row
                            label={t("mollieCheckout.returnPage.orderBox.course")}
                            value={courseContext.courseTitle}
                        />
                    )}
                    {courseContext.courseStartDate && (
                        <Row
                            label={t("mollieCheckout.returnPage.orderBox.courseStart")}
                            value={formatCourseStart(courseContext.courseStartDate)}
                        />
                    )}
                </div>
            )}

            {booking && (
                <>
                    <div className={`${css.bookingNumber} booking-number bookingNumber`}>
                        {t("mollieCheckout.returnPage.orderBox.bookingNumberLabel")}:{" "}
                        {booking.bookingNumber}
                    </div>
                    <div className={`${css.orderBox} order-box orderBox`}>
                        <Row
                            label={t("mollieCheckout.returnPage.orderBox.course")}
                            value={booking.courseName}
                        />
                        <Row
                            label={t("mollieCheckout.returnPage.orderBox.attendee")}
                            value={booking.attendeeFullName}
                        />
                        <Row
                            label={t("mollieCheckout.returnPage.orderBox.amount")}
                            value={formatAmount(booking.grossPrice)}
                        />
                        {booking.discount != null && booking.discount > 0 && (
                            <div data-testid="payment-return-discount">
                                <Row
                                    label={booking.discountName || t("discountCode.title")}
                                    value={`- ${formatAmount(booking.discount)} (${t("discountCode.alreadyDeducted")})`}
                                />
                            </div>
                        )}
                        <Row
                            label={t("mollieCheckout.returnPage.orderBox.method")}
                            value={booking.paymentMethodLabel}
                        />
                        {booking.paidAt && (
                            <Row
                                label={t("mollieCheckout.returnPage.orderBox.paidAt")}
                                value={formatPaidAt(booking.paidAt)}
                            />
                        )}
                    </div>
                </>
            )}

            {state === "failure" && status && status.failureReason && (
                <div className={`${css.reasonBox} reason-box`}>
                    <div>{t(mapMollieFailureReasonToKey(status.failureReason))}</div>
                    <span className={`${css.reservedBadge} reserved-badge`}>
                        {t("mollieCheckout.returnPage.failureCard.reservedBadge")}
                    </span>
                </div>
            )}

            {showBankTransferSection && status && (
                <div className={`${css.bankTransferSection} bank-transfer-section`}>
                    <BankTransferDetailsSection status={status} />
                </div>
            )}

            {booking && booking.bookerEmail && (
                <div className={`${css.emailHint} email-hint`}>
                    <Trans
                        i18nKey="mollieCheckout.returnPage.emailHint"
                        values={{ email: booking.bookerEmail }}
                        components={{ strong: <strong /> }}
                    />
                </div>
            )}

            {/*
              * T06 (#593): Pending-/Timeout-Karte boten keinerlei Bezahl-Ausweg —
              * ein hängender Checkout (z.B. abgebrochenes PayPal) wirkte wie eine
              * Sackgasse. Ein Retry ist hier bewusst NICHT möglich (Server-Guard
              * `current_attempt_active` gegen Doppelzahlung), daher stattdessen
              * der Hinweis auf die Expiry-/Reminder-Mail mit Retry-Link.
              * Bewusst KONDITIONAL formuliert (#104): Das Backend versendet die
              * Mail nur unter Gates (Tenant-Setting autoSendMollieReminderMails,
              * Cap 3/Buchung, 24h-Cooldown, Mailversand erfolgreich) und erzeugt
              * einen echten neuen Mollie-Link nur per Opt-in
              * autoIssuePaymentLinkOnExpiry — die UI kennt keines davon.
              * Bei sichtbaren Überweisungsdaten wäre der Hinweis irreführend
              * (Banktransfer soll schlicht überwiesen werden) → unterdrückt.
              */}
            {(state === "pending" || state === "timeout") && !showBankTransferSection && (
                <div
                    className={`${css.emailHint} email-hint expiry-hint`}
                    data-testid="payment-return-expiry-hint"
                >
                    {t("mollieCheckout.returnPage.expiryHint")}
                </div>
            )}

            {(state === "failure" || state === "choosePayment") && methodPicker && (
                <div
                    className={`${css.methodPickerSlot} method-picker-slot`}
                    data-testid="payment-return-method-picker-slot"
                >
                    {methodPicker}
                </div>
            )}

            <div className={`${css.actions} actions`}>
                {state === "success" && (
                    <>
                        <Link to={URL_BASE} className={`${css.euaPillBtn} eua-pill-btn`}>
                            {t("mollieCheckout.returnPage.backToCourses")}
                        </Link>
                        <Link to={URL_BASE} className={`${css.euaPrimaryBtn} eua-primary-btn`}>
                            {t("mollieCheckout.returnPage.successCard.secondaryCta")}
                        </Link>
                    </>
                )}

                {state === "failure" && (
                    <>
                        {/*
                          * Wenn ein Method-Picker eingeblendet ist (Cross-Device-Retry,
                          * Phase 26.1 Fix #3), bringt der Picker sein eigenes
                          * Submit-Verhalten mit — der primaere Retry-Button entfaellt
                          * dann, damit der Bucher nicht zwei konkurrierende CTAs sieht.
                          */}
                        {onRetry && !methodPicker && (
                            <Button
                                className={`${css.euaPrimaryBtn} eua-primary-btn`}
                                type="primary"
                                loading={!!retryLoading}
                                onClick={onRetry}
                            >
                                {t("mollieCheckout.returnPage.retry")}
                            </Button>
                        )}
                        {/*
                          * Ausgeloggter Bucher (Dispatch-Zahlungslink): primaerer
                          * "Anmelden & jetzt bezahlen"-Button. Nur sichtbar, wenn weder
                          * Retry-Button noch Method-Picker gerendert werden (das sind die
                          * eingeloggten Affordances). Der Klick fuehrt zum Login und nach
                          * dem Login zurueck auf diese Return-Page, wo dann der eingeloggte
                          * Bezahl-Pfad greift. Behebt den frueheren Sackgassen-Zustand.
                          */}
                        {onLoginToPay && !onRetry && !methodPicker && (
                            <Button
                                className={`${css.euaPrimaryBtn} eua-primary-btn`}
                                type="primary"
                                onClick={onLoginToPay}
                            >
                                {t("mollieCheckout.returnPage.loginToPay")}
                            </Button>
                        )}
                        <Link to={URL_BASE} className={`${css.euaPillBtn} eua-pill-btn`}>
                            {t("mollieCheckout.returnPage.payLater")}
                        </Link>
                    </>
                )}

                {/* Offener Versuch mit gueltigem Checkout (z.B. Reminder-Link geklickt,
                    waehrend ein neuer Zahlungslink offen ist): direkt dorthin fuehren
                    statt endlos "in Bearbeitung" zu zeigen (Support-Fall Krieler Welle). */}
                {(state === "pending" || state === "timeout") && status?.checkoutUrl && (
                    <Button
                        className={`${css.euaPrimaryBtn} eua-primary-btn`}
                        type="primary"
                        href={status.checkoutUrl}
                        data-testid="payment-return-pay-now"
                    >
                        {t("mollieCheckout.returnPage.payNow")}
                    </Button>
                )}

                {state === "pending" && (
                    <Link to={URL_BASE} className={`${css.euaPillBtn} eua-pill-btn`}>
                        {t("mollieCheckout.returnPage.backToCourses")}
                    </Link>
                )}

                {state === "timeout" && (
                    <>
                        {onManualRefetch && (
                            <Button
                                className={`${css.euaPrimaryBtn} eua-primary-btn`}
                                type="primary"
                                onClick={onManualRefetch}
                            >
                                {t("mollieCheckout.returnPage.timeoutCard.manualRefetch")}
                            </Button>
                        )}
                        <Link to={URL_BASE} className={`${css.euaPillBtn} eua-pill-btn`}>
                            {t("mollieCheckout.returnPage.backToCourses")}
                        </Link>
                    </>
                )}

                {state === "accessDenied" && (
                    <>
                        {/*
                          * Kein-Zugriff: Der eingeloggte Bucher ist nicht der
                          * Eigentuemer dieser Zahlung. Primaerer Ausweg ist der
                          * Konto-Wechsel (Login mit `fromUrl` zurueck auf diese
                          * Return-Page, danach greift der Eigentuemer-Pfad). KEIN
                          * Retry / Method-Picker — die gehoeren dem Eigentuemer.
                          */}
                        {onLoginToPay && (
                            <Button
                                className={`${css.euaPrimaryBtn} eua-primary-btn`}
                                type="primary"
                                onClick={onLoginToPay}
                            >
                                {t("mollieCheckout.returnPage.accessDeniedCard.switchAccount")}
                            </Button>
                        )}
                        <Link to={URL_BASE} className={`${css.euaPillBtn} eua-pill-btn`}>
                            {t("mollieCheckout.returnPage.backToCourses")}
                        </Link>
                    </>
                )}

                {/*
                  * No-Attempt-Fall (Konto → "Jetzt bezahlen"): Der Method-Picker
                  * (methodPickerSlot oben) bringt sein eigenes Submit-Verhalten mit.
                  * Als sekundaere Aktion nur der "Spaeter bezahlen"-Ausweg zurueck.
                  */}
                {state === "choosePayment" && (
                    <Link to={URL_BASE} className={`${css.euaPillBtn} eua-pill-btn`}>
                        {t("mollieCheckout.returnPage.payLater")}
                    </Link>
                )}
            </div>
        </div>
    );
}
